import { useRouter } from 'next/router';
import { Pressable } from 'react-native';

import { track as trackFunc } from '@lib/analytics';
import { formatDeliveryAddress } from '@lib/entities/Address';
import { getInformationalMessage } from '@lib/entities/Store';
import useDelivery from '@lib/hooks/useDelivery';
import useSchedule from '@lib/hooks/useSchedule';

import { Shop } from '@assets/svg';
import { DeliveryIcon, Edit } from '@assets/svg/revamp';

import Stack from '@components/Stack';
import Typography from '@components/Typography';

import { LocationHeaderProps } from '../LocationHeader/types';
import color from '@lib/utils/color';

const LocationHeaderLayout: React.FC<LocationHeaderProps> = ({
    onPress = () => {},
    track,
    showIcons = false,
    showDeliveryEta = false
}) => {
    const router = useRouter();
    const { store } = useSchedule();
    const { isPickup, deliveryAddress, isDelivery, deliveryCheck } = useDelivery();
    const addressLine = formatDeliveryAddress(deliveryAddress);

    // The light "primary.main" treatment with dark text is used wherever the
    // informational banner is attached to the header (home + menu, when the
    // selected store has an active message); everywhere else keeps the original
    // dark-blue header with white text. Mirrors the banner gate in SiteHeader.
    const isBannerPage = router.pathname === '/' || router.pathname.startsWith('/menu/');
    const hasBanner = isBannerPage && !!getInformationalMessage(store);

    if (!store) {
        // I don't think this scenario can happen. We redirect the user to home page if store is not selected.
        return (
            <Stack>
                <Typography variant="p3semi">Click to choose a location and continue</Typography>
            </Stack>
        );
    }

    return (
        <Pressable
            onPress={() => {
                trackFunc(track || 'location header at checkout');
                onPress();
            }}
        >
            <Stack
                row
                justifyContent={'center'}
                backgroundColor={hasBanner ? 'primary.main' : 'primary.dark'}
                sx={
                    hasBanner
                        ? { borderBottomWidth: 2, borderBottomColor: color('primary.dark') }
                        : undefined
                }
            >
                <Stack gap={10} row>
                    {showIcons && (
                        <>
                            {isPickup && <Shop />}
                            {isDelivery && <DeliveryIcon />}
                        </>
                    )}
                    {isPickup && (
                        <Stack row gap={5} center sx={{ height: 50 }}>
                            <Typography color={hasBanner ? undefined : 'white'} variant={'p3semi'}>
                                Pickup from: {store?.name} &nbsp;
                            </Typography>
                            <Typography
                                color={hasBanner ? undefined : 'white'}
                                variant={'p3semi'}
                                numberOfLines={1}
                                ellipsizeMode={'tail'}
                                underline
                                style={hasBanner ? undefined : { textDecorationColor: 'white' }}
                            >
                                Change
                            </Typography>
                        </Stack>
                    )}
                    {isDelivery && (
                        <Stack row gap={5} center sx={{ height: 50 }} nativeID="delivery-address">
                            {/* We do not have the address alias details on the frontend */}
                            <Typography color={'white'} variant={'p3'}>
                                Delivery to:
                            </Typography>
                            <Typography
                                color={'white'}
                                variant={'p3'}
                                numberOfLines={1}
                                ellipsizeMode={'tail'}
                            >
                                {addressLine}
                            </Typography>
                            <Typography
                                color={'white'}
                                variant={'p3semi'}
                                numberOfLines={1}
                                ellipsizeMode={'tail'}
                                underline
                                style={{ textDecorationColor: 'white' }}
                            >
                                Change
                            </Typography>
                            {showDeliveryEta && deliveryCheck && (
                                <Stack sx={{ mt: 10 }}>
                                    <Typography variant={'p4'} bold numberOfLines={1}>
                                        Delivery ETA:{' '}
                                        <Typography variant={'p4'}>
                                            {deliveryCheck.waitTime}
                                        </Typography>
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Stack>
                {showIcons && <Edit />}
            </Stack>
        </Pressable>
    );
};

export default LocationHeaderLayout;
